import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Wallet, Plus, ArrowUp, ArrowDown, TrendingUp, Calendar, Filter, DollarSign, History } from 'lucide-react';

const TYPE_LABELS = {
  fund_added: 'Fund Added',
  investment_return: 'Investment Return',
  manual_credit: 'Manual Credit',
  manual_debit: 'Manual Debit',
  withdrawal_paid: 'Withdrawal Paid',
  other_expense: 'Other Expense',
  other_income: 'Other Income'
};

export default function FundManagement() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('overview');
  const [addModal, setAddModal] = useState(false);
  const [txnModal, setTxnModal] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [addDesc, setAddDesc] = useState('');
  const [txnForm, setTxnForm] = useState({ amount: '', direction: 'out', type: 'other_expense', description: '' });
  const [loading, setLoading] = useState(false);

  const fetchSummary = async () => {
    try {
      const params = { period };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axios.get('/api/admin/fund/summary', { params });
      setSummary(res.data);
    } catch (e) {}
  };

  const fetchHistory = async () => {
    try {
      const params = { page, limit: 30 };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axios.get('/api/admin/fund/history', { params });
      setHistory(res.data.transactions || []);
      setTotal(res.data.total || 0);
    } catch (e) {}
  };

  useEffect(() => { fetchSummary(); fetchHistory(); }, [period, startDate, endDate, page]);

  const handleAddFunds = async () => {
    if (!addAmount || parseFloat(addAmount) <= 0) return toast.error('Valid amount daalo');
    setLoading(true);
    try {
      await axios.post('/api/admin/fund/add-funds', { amount: addAmount, description: addDesc });
      toast.success('Funds added!');
      setAddModal(false); setAddAmount(''); setAddDesc('');
      fetchSummary(); fetchHistory();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed');
    } finally { setLoading(false); }
  };

  const handleTxn = async () => {
    setLoading(true);
    try {
      await axios.post('/api/admin/fund/transaction', txnForm);
      toast.success('Transaction recorded!');
      setTxnModal(false); setTxnForm({ amount: '', direction: 'out', type: 'other_expense', description: '' });
      fetchSummary(); fetchHistory();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed');
    } finally { setLoading(false); }
  };

  const fmt = n => `$${(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Wallet className="w-6 h-6 text-primary-400" /> Fund Management
            </h1>
            <p className="text-gray-400 mt-1">Admin wallet — track all fund movements.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setTxnModal(true)}
              className="bg-dark-700 border border-white/10 text-gray-300 hover:text-white px-4 py-2.5 rounded-xl text-sm flex items-center gap-2">
              <History className="w-4 h-4" /> Record Transaction
            </button>
            <button onClick={() => setAddModal(true)}
              className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Funds
            </button>
          </div>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Current Balance', value: fmt(summary?.balance), icon: Wallet, color: 'text-primary-400', bg: 'bg-primary-500/20' },
            { label: 'Total Added', value: fmt(summary?.totalAdded), icon: ArrowDown, color: 'text-green-400', bg: 'bg-green-500/20' },
            { label: 'Total Paid Out', value: fmt(summary?.totalPaidOut), icon: ArrowUp, color: 'text-red-400', bg: 'bg-red-500/20' },
            { label: 'Net (Period)', value: fmt(summary?.periodStats?.net), icon: TrendingUp, color: summary?.periodStats?.net >= 0 ? 'text-primary-400' : 'text-red-400', bg: 'bg-blue-500/20' }
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center mb-3`}>
                <Icon className={`w-5 h-5 ${color}`} />
              </div>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-gray-400 text-sm mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Date Filter */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-4 mb-6 flex flex-wrap gap-3 items-end">
          <div className="flex gap-2">
            {['week','month','year'].map(p => (
              <button key={p} onClick={() => { setPeriod(p); setStartDate(''); setEndDate(''); }}
                className={`px-3 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${period===p && !startDate ? 'bg-primary-600 text-white' : 'bg-dark-600 text-gray-400'}`}>
                {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'This Year'}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-end">
            <div><label className="block text-xs text-gray-400 mb-1">From</label>
              <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPeriod(''); }}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none" /></div>
            <div><label className="block text-xs text-gray-400 mb-1">To</label>
              <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPeriod(''); }}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none" /></div>
            {(startDate||endDate) && <button onClick={() => { setStartDate(''); setEndDate(''); setPeriod('month'); }} className="text-gray-400 hover:text-white text-sm">Clear</button>}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview','monthly','history'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${tab===t ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 border border-white/5'}`}>
              {t === 'overview' ? 'Overview' : t === 'monthly' ? 'Monthly Trend' : 'Transaction History'}
            </button>
          ))}
        </div>

        {/* Overview Chart */}
        {tab === 'overview' && summary?.monthly?.length > 0 && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
            <h3 className="text-white font-semibold mb-5">Income vs Expense</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={summary.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="period" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1a2540', border: '1px solid #22c55e33', borderRadius: '12px', color: '#e2e8f0' }} formatter={v => [`$${v.toFixed(2)}`, '']} />
                <Legend wrapperStyle={{ color: '#9ca3af', fontSize: 12 }} />
                <Bar dataKey="in" fill="#22c55e" name="Income" radius={[4,4,0,0]} />
                <Bar dataKey="out" fill="#ef4444" name="Expense" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Monthly Table */}
        {tab === 'monthly' && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
            <div className="p-5 border-b border-white/5"><h3 className="text-white font-semibold">Monthly Summary</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/5">
                  {['Month','Income','Expense','Net'].map(h => <th key={h} className="px-4 py-3 text-gray-400 font-medium text-left">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(summary?.monthly || []).length === 0
                    ? <tr><td colSpan={4} className="text-center py-8 text-gray-500">No data</td></tr>
                    : (summary?.monthly || []).reverse().map((m, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/2">
                        <td className="px-4 py-3 text-white font-medium">{m.period}</td>
                        <td className="px-4 py-3 text-green-400 font-semibold">{fmt(m.in)}</td>
                        <td className="px-4 py-3 text-red-400 font-semibold">{fmt(m.out)}</td>
                        <td className={`px-4 py-3 font-bold ${m.in - m.out >= 0 ? 'text-primary-400' : 'text-red-400'}`}>{fmt(m.in - m.out)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* History */}
        {tab === 'history' && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
            <div className="p-5 border-b border-white/5 flex justify-between items-center">
              <h3 className="text-white font-semibold">Transaction History ({total})</h3>
            </div>
            <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
              {history.length === 0
                ? <p className="text-center text-gray-500 py-12">No transaction history.</p>
                : history.map((t, i) => (
                  <div key={i} className="px-4 py-3 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${t.direction==='in' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      {t.direction === 'in' ? <ArrowDown className="w-4 h-4 text-green-400" /> : <ArrowUp className="w-4 h-4 text-red-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{TYPE_LABELS[t.type] || t.type}</p>
                      <p className="text-gray-500 text-xs">{t.description}</p>
                      {t.relatedUserInfo && <p className="text-gray-600 text-xs">{t.relatedUserInfo.name} ({t.relatedUserInfo.email})</p>}
                      <p className="text-gray-600 text-xs">{new Date(t.createdAt).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${t.direction==='in' ? 'text-green-400' : 'text-red-400'}`}>
                        {t.direction==='in' ? '+' : '-'}{fmt(t.amount)}
                      </p>
                      <p className="text-gray-500 text-xs">Bal: {fmt(t.balanceAfter)}</p>
                    </div>
                  </div>
                ))}
            </div>
            {total > 30 && (
              <div className="p-4 flex justify-center gap-2">
                <button disabled={page===1} onClick={() => setPage(p => p-1)} className="px-3 py-1.5 bg-dark-600 text-gray-400 rounded-lg text-sm disabled:opacity-40">Prev</button>
                <span className="text-gray-400 text-sm py-1.5">Page {page}</span>
                <button disabled={page*30>=total} onClick={() => setPage(p => p+1)} className="px-3 py-1.5 bg-dark-600 text-gray-400 rounded-lg text-sm disabled:opacity-40">Next</button>
              </div>
            )}
          </div>
        )}

        {/* Add Funds Modal */}
        {addModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-700 border border-white/10 rounded-2xl w-full max-w-md p-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-primary-400" /> Add Funds</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1.5">Amount ($)</label>
                  <input type="number" value={addAmount} onChange={e => setAddAmount(e.target.value)}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                    placeholder="e.g. 5000" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1.5">Description (optional)</label>
                  <input type="text" value={addDesc} onChange={e => setAddDesc(e.target.value)}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                    placeholder="e.g. Monthly fund deposit" />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setAddModal(false)} className="flex-1 bg-dark-600 border border-white/10 text-gray-400 hover:text-white py-3 rounded-xl">Cancel</button>
                <button onClick={handleAddFunds} disabled={loading}
                  className="flex-1 bg-primary-600 hover:bg-primary-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : 'Add Funds'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Transaction Modal */}
        {txnModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-700 border border-white/10 rounded-2xl w-full max-w-md p-6">
              <h3 className="text-white font-bold mb-4">Record Transaction</h3>
              <div className="space-y-3">
                <div className="flex gap-2">
                  {['in','out'].map(d => (
                    <button key={d} onClick={() => setTxnForm(f => ({ ...f, direction: d, type: d==='in'?'other_income':'other_expense' }))}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold capitalize transition-colors ${txnForm.direction===d ? d==='in'?'bg-green-600 text-white':'bg-red-600 text-white' : 'bg-dark-600 text-gray-400'}`}>
                      {d === 'in' ? '+ Income' : '- Expense'}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Type</label>
                  <select value={txnForm.type} onChange={e => setTxnForm(f => ({ ...f, type: e.target.value }))}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none">
                    {Object.entries(TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Amount ($)</label>
                  <input type="number" value={txnForm.amount} onChange={e => setTxnForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Description</label>
                  <input type="text" value={txnForm.description} onChange={e => setTxnForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none" />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setTxnModal(false)} className="flex-1 bg-dark-600 border border-white/10 text-gray-400 hover:text-white py-3 rounded-xl">Cancel</button>
                <button onClick={handleTxn} disabled={loading}
                  className="flex-1 bg-primary-600 hover:bg-primary-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
